import { IActivityLog } from "@entities/activity/lib/types";
import CampaignDraftDrawer from "@widgets/CampaignDraftDrawer";
import CampaignScriptingDrawer from "@widgets/CampaignScriptingDrawer";
import { Avatar, Button, Flex, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { CreditCard, LayoutGrid, Megaphone, Shield, User } from "lucide-react";
import { useState } from "react";
import styles from "./styles.module.scss";

const { Text } = Typography;

const getIcon = (type: IActivityLog["type"]) => {
  switch (type) {
    case "Campaign":
      return <Megaphone size={16} />;
    case "Brand-Organisation-Finance":
      return <CreditCard size={16} />;
    case "Brand-Organisation-Security":
      return <Shield size={16} />;
    case "Brand-Organisation-Team":
      return <User size={16} />;
    case "Brand-Organisation-Ad-Rights":
      return <LayoutGrid size={16} />;
    default:
      return <LayoutGrid size={16} />;
  }
};

export const ActivityItem = ({
  activity,
  datePosition,
}: {
  activity: IActivityLog;
  datePosition?: "start" | "end";
}) => {
  const [isDraftDrawerOpen, setIsDraftDrawerOpen] = useState<boolean>(false);
  const [isScriptDrawerOpen, setIsScriptDrawerOpen] = useState<boolean>(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [scriptingId, setScriptingId] = useState<string | null>(null);

  return (
    <div className={styles.activityItem}>
      <div className={styles.iconWrapper}>
        <Avatar
          className={`${styles.icon} ${styles[activity.type]}`}
          icon={getIcon(activity.type)}
          size={35}
          shape="square"
        />
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <Flex
            justify={"space-between"}
            align={datePosition === "end" ? "center" : "start"}
            vertical={datePosition === "start" ? true : false}
            wrap
            style={{ width: "100%" }}
          >
            <div>
              <Text strong className={styles.title}>
                {activity?.actorTitle}
              </Text>{" "}
              {activity?.actorAction}{" "}
              <Text strong className={styles.title}>
                {activity?.actorActionDesc}
              </Text>
            </div>
            {activity?.actionByType === "admin-user" && (
              <div>Action By: Cloutflow Team</div>
            )}

            {(activity?.scriptingId || activity?.draftingId) &&
            datePosition === "start" ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => {
                  if (activity?.draftingId) {
                    setIsDraftDrawerOpen(true);
                    setDraftId(activity?.draftingId || "");
                  } else if (activity?.scriptingId) {
                    setIsScriptDrawerOpen(true);
                    setScriptingId(activity?.scriptingId || "");
                  }
                }}
              >
                {`View ${activity?.draftingId ? "Draft" : activity?.scriptingId ? "Script" : ""}`}
              </Button>
            ) : null}
            <Text
              type="secondary"
              className={styles.time}
              style={{ paddingRight: 16 }}
            >
              {dayjs(activity?.activityDate).format("MMM DD YYYY [at] hh:mm A")}
            </Text>
          </Flex>
        </div>
        <Flex vertical>
          {(activity?.scriptingId || activity?.draftingId) &&
          (!datePosition || datePosition === "end") ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, width: "fit-content" }}
              onClick={() => {
                if (activity?.draftingId) {
                  setIsDraftDrawerOpen(true);
                  setDraftId(activity?.draftingId || "");
                } else if (activity?.scriptingId) {
                  setIsScriptDrawerOpen(true);
                  setScriptingId(activity?.scriptingId || "");
                }
              }}
            >
              {`View ${activity?.draftingId ? "Draft" : activity?.scriptingId ? "Script" : ""}`}
            </Button>
          ) : null}
          <Space size="small" className={styles.meta}>
            {activity.brand && <Tag color="blue">{activity?.brand?.name}</Tag>}
            <Space.Compact>
              {activity.remarks &&
                activity?.remarks?.map((remark) => (
                  <Tag
                    key={remark}
                    style={{
                      color: "#697386",
                    }}
                  >
                    {remark}
                  </Tag>
                ))}
            </Space.Compact>
          </Space>
        </Flex>
      </div>
      <CampaignDraftDrawer
        open={isDraftDrawerOpen}
        onClose={() => setIsDraftDrawerOpen(false)}
        draftId={draftId || ""}
      />

      <CampaignScriptingDrawer
        open={isScriptDrawerOpen}
        onClose={() => setIsScriptDrawerOpen(false)}
        scriptingId={scriptingId || ""}
      />
    </div>
  );
};
